import apiV2 from '../api/v2';
import type { PriorityLevel } from '../components/work-queue/priority/priority';

export interface PatientRow {
    id: string;
    name: string;
    practiceName: string | null;
    practiceDoctor: string | null;
    internalCaseStatus: string | null;
    caseStatus: string | null;
    actionRequired: string | null;
    profileThumbnailUrl: string | null;
    version: string | null;
    // Always present (backend defaults a null patient_monday_data.priority to
    // 'normal') - same fallback the Work Queue applies, so a patient shows the
    // identical priority on both screens by construction.
    priority: PriorityLevel;
}

export interface PatientsPageMeta {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface PatientsPageResponse {
    data: PatientRow[];
    meta: PatientsPageMeta;
}

export interface PatientsParams {
    page: number;
    limit: number;
    sort: 'patient' | 'practice';
    order: 'asc' | 'desc';
    // Filters by practice NAME (may match several practices sharing a name).
    // Prefer practiceIds - names are not unique.
    practice?: string[];
    // Exact practice ids (multi) - the Patients page practice filter.
    practiceIds?: string[];
    caseStatus?: string[];
    // Workflow macro ids (StatusMeta.macro), e.g. 'design', 'manufacturing'.
    macro?: string[];
    actionRequired?: string[];
    // Raw internal_case_status values - the Practice detail Patients tab's
    // Phase/Internal step pills resolve to this one param (see
    // resolveInternalStatusFilter).
    internalStatus?: string[];
    search?: string;
    // Exact practice id - a distinct, separate filter used by the
    // patient-linking picker (Accounts > Communications), which already
    // knows the practice's id and has no use for the name-based `practice`
    // filter above.
    practiceId?: string;
}

export interface PatientFilterOptions {
    // No practice fields: practice options come from GET /v2/practices/options
    // (usePracticeOptions) - id-keyed, so duplicate names can't collapse.
    // No phase field either: phase options come from the workflow taxonomy
    // (/workflows/meta via useStatusCatalog / useMeta), not this endpoint.
    caseStatuses: string[];
    actionsRequired: string[];
}

export const fetchPatientsPage = async (params: PatientsParams): Promise<PatientsPageResponse> => {
    // Filter values are raw names/labels that may contain a comma, so they stay
    // arrays - apiV2 serializes them as repeated params.
    const query: Record<string, string | string[]> = {
        page: String(params.page),
        limit: String(params.limit),
        sort: params.sort,
        order: params.order
    };
    if (params.practice?.length) query.practice = params.practice;
    if (params.practiceIds?.length) query.practiceIds = params.practiceIds;
    if (params.caseStatus?.length) query.caseStatus = params.caseStatus;
    if (params.macro?.length) query.macro = params.macro;
    if (params.actionRequired?.length) query.actionRequired = params.actionRequired;
    if (params.internalStatus?.length) query.internalStatus = params.internalStatus;
    if (params.search?.trim()) query.search = params.search.trim();
    if (params.practiceId) query.practiceId = params.practiceId;

    const response = await apiV2.get('/patients', { params: query });
    return response.data;
};

export const fetchPatientFilterOptions = async (): Promise<PatientFilterOptions> => {
    const response = await apiV2.get('/patients/filters');
    return response.data.data;
};
